import React from "react";
import { useTranslation } from "react-i18next";
import { Day } from "../../types/Day";
import { DAY_NAME_KEYS, ALL_DAYS } from "../../utils/constants";

interface DaysCheckboxGroupProps {
	selectedDays: Day[];
	onChange: (days: Day[]) => void;
	label?: string;
	error?: string;
	disabled?: boolean;
}

const DaysCheckboxGroup: React.FC<DaysCheckboxGroupProps> = ({
	selectedDays,
	onChange,
	label,
	error,
	disabled = false,
}) => {
	const { t } = useTranslation();

	const handleToggleDay = (day: Day) => {
		if (disabled) return;

		if (selectedDays.includes(day)) {
			onChange(selectedDays.filter((d) => d !== day));
		} else {
			onChange([...selectedDays, day].sort((a, b) => a - b));
		}
	};

	return (
		<div className="w-full">
			{label && (
				<label className="block text-gray-700 text-sm font-semibold mb-3">
					{label}
				</label>
			)}

			<div className="flex flex-wrap gap-3">
				{ALL_DAYS.map((day) => {
					const isSelected = selectedDays.includes(day);
					return (
						<label
							key={day}
							className={`flex items-center cursor-pointer px-4 py-2 rounded-lg border-2 transition-all duration-200 outline-none focus:outline-none ${
								isSelected
									? "bg-gradient-to-r from-primary-50 to-primary-100 border-primary-400 text-primary-700 font-semibold shadow-sm"
									: "bg-white border-gray-200 text-gray-700 hover:border-primary-300 hover:bg-primary-50/50"
							} ${
								disabled ? "cursor-not-allowed opacity-50" : ""
							}`}
						>
							<input
								type="checkbox"
								checked={isSelected}
								onChange={() => handleToggleDay(day)}
								disabled={disabled}
								className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-0 focus:outline-none disabled:cursor-not-allowed mr-2"
							/>
							<span>{t(DAY_NAME_KEYS[day])}</span>
						</label>
					);
				})}
			</div>

			{error && <p className="mt-1 text-sm text-red-600">{error}</p>}
		</div>
	);
};

export default DaysCheckboxGroup;
